import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Card } from "react-bootstrap";

import Alert from "components/Common/Alert";
import SVGIcon from "components/Icons/SVGIcon";
import { Tooltip } from "components/Common/Tooltip/";
import Loading from "components/Loading";

import {
  loadSiteSchedulesStart,
  setSiteSchedulesCoreEditing,
  setSiteSchedulesCoreSaving,
  setSiteSchedulesCoreResetting,
} from "services/redux/actions/singleSites";

import debounce from "lodash/debounce";
import moment from "services/locale/momentInit.js";
import conxtdOut from "apis/conxtdOut";

import "./CoreSchedule.scss";

export default function CoreSchedule({ site, enterprise_id, weekdays, getFormattedIdealAndOffsets }) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);
  const loading = useSelector((state) => state.sites.current.schedules.loading);
  const coreRules = useSelector((state) => state.sites.current.schedules.core.days);
  const { editing, saving, resetting } = useSelector((state) => state.sites.current.schedules.core);

  const [coreOpenRules, setCoreOpenRules] = useState(null);
  const [coreCloseRules, setCoreCloseRules] = useState(null);

  const eventCode = "C172";

  const getFormattedCoreRules = (type) => {
    const rules = {};

    weekdays.forEach(day => {
      if (coreRules[day] && coreRules[day][type]) {
        const ruleData = coreRules[day][type];

        const {
          idealEventHour,
          idealTimeInMiutes,
          earlyOffsetTime,
          earlyOffsetMinutes,
          lateOffsetTime,
          lateOffsetMinutes,
        } = getFormattedIdealAndOffsets(ruleData.ideal_event_hour, ruleData.early_offset, ruleData.late_offset);

        const ruleId = ruleData.id || null;
        const isDefault = ruleData.default === true;
        const reminder = Number(ruleData.reminder) === 1;
        const notExpected = ruleData.not_expected === true;
        const notExpectedId = ruleData.not_expected_id || null;

        rules[day] = {
          default: isDefault,
          id: ruleId,
          delete: false,
          showNextDayMessage: false,
          //early offset
          originalEarlyOffsetMinutes: earlyOffsetMinutes, // store the original values alongside the values that might get updated so we can quickly see what's been changed
          earlyOffsetMinutes: earlyOffsetMinutes,
          earlyOffsetTime: earlyOffsetTime,
          // ideal event hour
          originalIdealEventHour: idealEventHour,
          idealEventHour: idealEventHour,
          idealTimeInMiutes: idealTimeInMiutes,
          // late offset
          originalLateOffsetMinutes: lateOffsetMinutes,
          lateOffsetMinutes: lateOffsetMinutes,
          lateOffsetTime: lateOffsetTime,
          // reminder
          originalReminder: reminder,
          reminder: reminder,
          // not expected
          notExpectedId: notExpectedId,
          originalNotExpected: notExpected,
          notExpected: notExpected,
        }
      } else {
        rules[day] = {
          default: true,
          id: null,
          delete: false,
          showNextDayMessage: false,
          //early offset
          originalEarlyOffsetMinutes: 0,
          earlyOffsetMinutes: 0,
          earlyOffsetTime: "00:00",
          // ideal event hour
          originalIdealEventHour: "00:00",
          idealEventHour: "00:00",
          idealTimeInMiutes: 0,
          // late offset
          originalLateOffsetMinutes: 0,
          lateOffsetMinutes: 0,
          lateOffsetTime: "00:00",
          // reminder
          originalReminder: false,
          reminder: false,
          // not expected
          notExpectedId: null,
          originalNotExpected: false,
          notExpected: false,
        }
      }
    });

    return rules;
  }

  useEffect(() => {
    setCoreOpenRules(getFormattedCoreRules("open", weekdays));
    setCoreCloseRules(getFormattedCoreRules("close", weekdays));
  }, [coreRules]);

  const setShowNextDayMessageDebounced = useCallback(debounce((day, show) => {
    // only show the Next day message on the close rule
    setCoreCloseRules(prevState => {
      return {
        ...prevState,
        [day]: {
          ...prevState[day],
          showNextDayMessage: show,
        }
      }
    });
  }, 300), []);

  const changeRuleValue = (type, day, field, value) => {
    const setRulesFunction = type === "open" ? setCoreOpenRules : setCoreCloseRules;

    if (field === "idealEventHour") {
      const earlyOffsetMinutes = type === "open" ? coreOpenRules[day].earlyOffsetMinutes : coreCloseRules[day].earlyOffsetMinutes;
      const lateOffsetMinutes = type === "open" ? coreOpenRules[day].lateOffsetMinutes : coreCloseRules[day].lateOffsetMinutes;

      const {
        idealTimeInMiutes,
        earlyOffsetTime,
        lateOffsetTime,
      } = getFormattedIdealAndOffsets(value, earlyOffsetMinutes, lateOffsetMinutes);

      setRulesFunction(prevState => {
        return {
          ...prevState,
          [day]: {
            ...prevState[day],
            [field]: value,
            idealTimeInMiutes: idealTimeInMiutes,
            earlyOffsetTime: earlyOffsetTime,
            lateOffsetTime: lateOffsetTime,
          }
        }
      });

      // check whether we need to show the Next day message. we need to check when we edit either open or close
      if (type === "open") { 
        setShowNextDayMessageDebounced(day, coreCloseRules[day].idealTimeInMiutes <= idealTimeInMiutes);
      } else {
        setShowNextDayMessageDebounced(day, idealTimeInMiutes <= coreOpenRules[day].idealTimeInMiutes);
      }

    } else if (field === "earlyOffsetMinutes") {
      const idealEventHour = type === "open" ? coreOpenRules[day].idealEventHour : coreCloseRules[day].idealEventHour;
      const lateOffsetMinutes = type === "open" ? coreOpenRules[day].lateOffsetMinutes : coreCloseRules[day].lateOffsetMinutes;
      
      const {
        earlyOffsetTime,
      } = getFormattedIdealAndOffsets(idealEventHour, value, lateOffsetMinutes);

      setRulesFunction(prevState => {
        return {
          ...prevState,
          [day]: {
            ...prevState[day],
            [field]: value,
            earlyOffsetTime: earlyOffsetTime,
          }
        }
      });
    } else if (field === "lateOffsetMinutes") {
      const earlyOffsetMinutes = type === "open" ? coreOpenRules[day].earlyOffsetMinutes : coreCloseRules[day].earlyOffsetMinutes;
      const idealEventHour = type === "open" ? coreOpenRules[day].idealEventHour : coreCloseRules[day].idealEventHour;

      const {
        lateOffsetTime,
      } = getFormattedIdealAndOffsets(idealEventHour, earlyOffsetMinutes, value);

      setRulesFunction(prevState => {
        return {
          ...prevState,
          [day]: {
            ...prevState[day],
            [field]: value,
            lateOffsetTime: lateOffsetTime,
          }
        }
      });
    } else {
      setRulesFunction(prevState => {
        return {
          ...prevState,
          [day]: {
            ...prevState[day],
            [field]: value,
          }
        }
      });
    }
  }

  const ruleIsEdited = (rule) => {
    if (rule.idealEventHour != rule.originalIdealEventHour) {
      return true;
    }
    if (rule.reminder != rule.originalReminder) {
      return true;
    }
    if (rule.earlyOffsetMinutes != rule.originalEarlyOffsetMinutes) {
      return true;
    }
    if (rule.lateOffsetMinutes != rule.originalLateOffsetMinutes) {
      return true;
    }
    return false;
  }

  const saveRules = async () => {
    let coreRules = [];
    
    if (coreOpenRules) {
      coreRules = Object.keys(coreOpenRules).map((key) => {
        return {
          day: weekdays.indexOf(key) + 1,
          ruleTypeId: 1,
          ...coreOpenRules[key]
        }
      });
    }

    if (coreCloseRules) {
      coreRules = coreRules.concat(Object.keys(coreCloseRules).map((key) => {
        return {
          day: weekdays.indexOf(key) + 1,
          ruleTypeId: 2,
          ...coreCloseRules[key]
        }
      }));
    }

    const requestsDetails = [];

    coreRules.forEach((rule) => {
      const notExpectedSet = (rule.notExpected === true) && (rule.originalNotExpected === false);
      const notExpectedUnset = (rule.notExpected === false) && (rule.originalNotExpected === true);

      if (rule.delete) {
        const params = {
          rule_type_id: rule.ruleTypeId,
          sudo_site_id: site.sudo_site_id,

          day_of_week: rule.day,
        }

        requestsDetails.push({url: `/analyticx/scheduler-rules/delete/${rule.id}`, params: params, method: "DELETE"});
      }
      
      if (notExpectedSet) {
        if (rule.ruleTypeId === 1) { // only need to add the not expected rule once, so only do it as we process open
          const params = {
            rule_type_id: 4,
            sudo_site_id: site.sudo_site_id,

            day_of_week: rule.day,
          }
          if (enterprise_id) {
            params["enterprise_id"] = enterprise_id;
          }

          requestsDetails.push({url: `/analyticx/scheduler-rules/add`, params: params, method: "POST"});
        }
      } else {
        if (notExpectedUnset) {
          if (rule.ruleTypeId === 1) { // only need to remove the not expected rule once, so only do it as we process open
            const params = {
              rule_type_id: 4,
              sudo_site_id: site.sudo_site_id,

              day_of_week: rule.day,
            }

            requestsDetails.push({url: `/analyticx/scheduler-rules/delete/${rule.notExpectedId}`, params: params, method: "DELETE"});
          }
        }

        if (ruleIsEdited(rule) && !rule.delete) {
          const params = {
            rule_type_id: rule.ruleTypeId,
            sudo_site_id: site.sudo_site_id,

            day_of_week: rule.day,
            early_offset: rule.earlyOffsetMinutes,
            ideal_event_hour: rule.idealEventHour,
            late_offset: rule.lateOffsetMinutes,
            reminder: rule.reminder,
          }
          if (enterprise_id) {
            params["enterprise_id"] = enterprise_id;
          }

          if (rule.default) {
            // it's a default rule, so add new core rule to override that default.
            // in this case we pass no start or end date to show that it's a core rule

            requestsDetails.push({url: `/analyticx/scheduler-rules/add`, params: params, method: "POST"});
          } else {
            // it's not a default, so it already exists as a separate core rule, so update that rule
            params["id"] = rule.id;

            requestsDetails.push({url: `/analyticx/scheduler-rules/edit/${rule.id}`, params: params, method: "POST"});
          }
        }
      }
    });

    if (requestsDetails.length > 0) {
      Promise.allSettled(requestsDetails.map((requestDetails) => {
        if (requestDetails.method === "DELETE") {
          return conxtdOut.delete(requestDetails.url, requestDetails.params);
        } else {
          return conxtdOut.post(requestDetails.url, requestDetails.params);
        }
      }))
      .then((results) => {
        const addEventUrl = enterprise_id
          ? `/Events/add/${site.id}`
          : `/Events/add/${site.id}/ss`;

        let addEventText = "";

        const getTextLines = (requestsDetails, results) => {
          const textLines = [];
          requestsDetails.forEach((requestDetails, index) => {
            let lineText = "";
            let ruleTypeText = "";
            switch (requestDetails.params.rule_type_id) {
              case 1:
                ruleTypeText = "Open";
                break;
              case 2:
                ruleTypeText = "Close";
                break;
              case 4:
                ruleTypeText = "Not expected";
            }

            if (textLines[requestDetails.params.day_of_week]) {
              // we have an existing line of text for this day, so add to it on the same line
              lineText = textLines[requestDetails.params.day_of_week] + " /";
            } else {
              // no existing text, so start a new line
              lineText = lineText + "\n" + weekdays[requestDetails.params.day_of_week - 1];
            }
            lineText = lineText + " " + ruleTypeText;

            if (requestDetails.method === "DELETE") {
              lineText = lineText + " rule deleted";
            } else {
              if (requestDetails.params.rule_type_id !== 4) { // not Not expected
                lineText = lineText + " " + requestDetails.params.ideal_event_hour;
                lineText = lineText + ", early " + requestDetails.params.early_offset;
                lineText = lineText + ", late " + requestDetails.params.late_offset;
              }
            }
            
            if (results[index].status === "rejected") {
              lineText = lineText + " FAILED";
            }

            textLines[requestDetails.params.day_of_week] = lineText;
          });

          return textLines;
        }

        // check if all the requests were successful
        if (results.every((result) => result.status === "fulfilled")) {
          Alert({
            text: "Rules updated successfully.",
            icon: "success",
            timerProgressBar: true,
            timer: 3000,
          });

          dispatch(loadSiteSchedulesStart({
            sudo_site_id: site.sudo_site_id,
            enterprise_id: enterprise_id,
          }));

          dispatch(setSiteSchedulesCoreSaving({ value: false }));
          dispatch(setSiteSchedulesCoreEditing({ value: false }));

          addEventText = `${user.firstName + " " + user.lastName} made changes to ${requestsDetails.length} core schedule${requestsDetails.length > 1 ? "s" : ""}.`;

          const textLines = getTextLines(requestsDetails, results);

          textLines.forEach(textLine => {
            addEventText = addEventText + textLine;
          })

        } else if (results.every((result) => result.status === "rejected")) {
          // all requests failed
          Alert({
            text: "Failed to save rules.",
            icon: "error",
            timerProgressBar: true,
            timer: 3000,
          });
        } else {
          // at least one of the requests failed
          Alert({
            text: "Some edits failed to save.",
            icon: "error",
            timerProgressBar: true,
            timer: 3000,
          });

          addEventText = `${user.firstName + " " + user.lastName} made changes to ${requestsDetails.length} core schedule${requestsDetails.length > 1 ? "s" : ""}, with failures.`;

          const textLines = getTextLines(requestsDetails, results);

          textLines.forEach(textLine => {
            addEventText = addEventText + textLine;
          })
        }

        // inject event to say user has edited rules
        try {
          conxtdOut.post(addEventUrl, {
            event: eventCode,
            zone: "",
            text: addEventText,
            time: moment.utc().format(),
          }).catch(error => {
            console.log(error);
          });
        } catch (error) {
          console.log(error);
        }
      })
      .catch((error) => {
        console.error("error:", error.message);
        Alert({
          text: "Error encountered while saving rules.",
          icon: "error",
          timerProgressBar: true,
          timer: 3000,
        });
      });
    } else {
      Alert({
        text: "No changes entered.",
        icon: "info",
        timerProgressBar: true,
        timer: 3000,
      });

      dispatch(setSiteSchedulesCoreSaving({ value: false }));
      dispatch(setSiteSchedulesCoreEditing({ value: false }));
    }
  };

  useEffect(() => {
    if (saving) {
      document.getElementById("core_schedule_form").requestSubmit(); // submit the form to take advantage of in-built validation on the time input.
                                                                     // requestSubmit also triggers the onSubmit of the form
    }
  }, [saving]);

  useEffect(() => {
    if (resetting) {
      setCoreOpenRules(getFormattedCoreRules("open", weekdays));
      setCoreCloseRules(getFormattedCoreRules("close", weekdays));
      dispatch(setSiteSchedulesCoreResetting({ value: false }));
      dispatch(setSiteSchedulesCoreSaving({ value: false }));
      dispatch(setSiteSchedulesCoreEditing({ value: false }));
    }
  }, [resetting]);

  return loading
    ? <div className="pt-4"><Loading center /></div>
    : <>
      <Form
        id="core_schedule_form"
        onSubmit={event => {
          event.preventDefault();
          saveRules();
        }}
      >
        <div className="pr-2 mb-2 core-schedule-headers-outer">
          <div className="core-schedule-day-outer">
            <div className="core-schedule-day-name-outer"></div>{/* day name placeholder */}
            <div className="pl-3 core-schedule-rule">
              <div className="core-schedule-rule-no-wrap-section">
                <div className="core-schedule-rule-icon" style={{ flexShrink: "0" }}></div>{/* open/close icon placeholder */}
                <div className="pl-3 core-schedule-rule-fields">
                  <div className="core-schedule-early-header">Early</div>{/* early offset */}
                  <div className="core-schedule-ideal-header">Ideal</div>{/* ideal event hour */}
                  <div className="core-schedule-late-header">Late</div>{/* late offset */}
                </div>
              </div>
              <div className="core-schedule-rule-fields">
                <div className="core-schedule-reminder-header">Reminder</div>{/* reminder */}
                <div className="core-schedule-delete-header">Delete Rule</div>{/* delete rule */}
              </div>
            </div>
          </div>
        </div>
        <div className="core-schedule-rules-scrollable">
          <div style={{ marginBottom: "50px" }}>
            {
              coreRules && weekdays.map((day, index) => {
                const dayNotExpected = coreOpenRules && coreOpenRules[day].notExpected; // not expected applies to both open and close together, so just need to check open

                return <Card
                  key={index}
                  bg="light"
                  className="pt-1 pr-2 pb-0 mr-1 mb-2 d-flex justify-content-start"
                >
                  <div className="core-schedule-day-outer">
                    <div className="pb-1 core-schedule-day-name-outer">
                      <div className="core-schedule-day-name">{day}</div>{/* day name */}
                      <div className="mt-2 mb-1 d-flex align-items-center">{/* not expected */}
                        <Form.Control
                          id={`open_not_expected_${day}`}
                          name={`open_not_expected_${day}`}
                          style={{ width: "1rem", height: "1rem", backgroundColor: "#fff" }}
                          type="checkbox"
                          checked={coreOpenRules && coreOpenRules[day].notExpected}
                          onChange={event => {
                            changeRuleValue("open", day, "notExpected", event.target.checked);
                            changeRuleValue("close", day, "notExpected", event.target.checked);
                          }}
                          disabled={!editing}
                        />
                        <Form.Label htmlFor={`open_not_expected_${day}`} className="pl-1 mb-0" style={{ fontWeight: "normal" }}>Not expected</Form.Label>
                      </div>
                    </div>
                    <div>
                      {coreOpenRules &&
                        <div className="pt-1 pl-3 core-schedule-rule">
                          <div className="core-schedule-rule-no-wrap-section">
                            <div className="pr-0 core-schedule-rule-icon">{/* open icon */}
                              <div
                                style={{ width: "2rem", height: "2rem", paddingLeft: "2px" }}
                              >
                                <SVGIcon type={dayNotExpected ? "dash" : "unlock"} color={dayNotExpected ? "#2B3E4E" : "#3397DB"} />
                              </div>
                            </div>
                            <div className="pl-3 core-schedule-rule-fields">
                              <div className="p-0 core-schedule-early-field">{/* early offset */}
                                <Tooltip 
                                  placement="top"
                                  description={`Early offset time: ${coreOpenRules[day].earlyOffsetTime}`}
                                >
                                  <Form.Control
                                    type="number"
                                    name={`open_early_offset_${day}`}
                                    value={dayNotExpected ? 0 : coreOpenRules[day].earlyOffsetMinutes}
                                    onChange={event => changeRuleValue("open", day, "earlyOffsetMinutes", event.target.value)}
                                    readOnly={!editing || dayNotExpected}
                                    style={ coreOpenRules[day].default ? {} : { border: "solid 1px #000" } }
                                  />
                                </Tooltip>
                              </div>
                              <div className="px-2 py-0 core-schedule-ideal-field">{/* ideal event hour */}
                                <Form.Control
                                  type="time"
                                  name={`open_ideal_event_hour_${day}`}
                                  value={dayNotExpected ? "00:00" : coreOpenRules[day].idealEventHour}
                                  onChange={event => changeRuleValue("open", day, "idealEventHour", event.target.value)}
                                  readOnly={!editing || dayNotExpected}
                                  style={ coreOpenRules[day].default ? {} : { border: "solid 1px #000" } }
                                />
                              </div>
                              <div className="p-0 core-schedule-late-field">{/* late offset */}
                                <Tooltip 
                                  placement="top"
                                  description={`Late offset time: ${coreOpenRules[day].lateOffsetTime}`}
                                >
                                  <Form.Control
                                    type="number"
                                    name={`open_late_offset_${day}`}
                                    value={dayNotExpected ? 0 : coreOpenRules[day].lateOffsetMinutes}
                                    onChange={event => changeRuleValue("open", day, "lateOffsetMinutes", event.target.value)}
                                    readOnly={!editing || dayNotExpected}
                                    style={ coreOpenRules[day].default ? {} : { border: "solid 1px #000" } }
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="my-2 core-schedule-rule-fields">
                            <div className="pl-3 py-0 core-schedule-reminder-field">{/* reminder */}
                              {/* <Form.Control
                                name={`open_reminder_${day}`}
                                style={{ width: "1rem", height: "1rem", backgroundColor: "#fff" }}
                                type="checkbox"
                                checked={dayNotExpected ? false : coreOpenRules[day].reminder}
                                onChange={event => changeRuleValue("open", day, "reminder", event.target.checked)}
                                disabled={!editing || dayNotExpected}
                              /> */}
                            </div>
                            <div className="pl-3 py-0 core-schedule-delete-field">{/* delete rule */}
                              <Form.Control
                                name={`open_delete_${day}`}
                                style={{ width: "1rem", height: "1rem", backgroundColor: "#fff" }}
                                type="checkbox"
                                checked={coreOpenRules && coreOpenRules[day].delete}
                                onChange={event => changeRuleValue("open", day, "delete", event.target.checked)}
                                disabled={!editing || dayNotExpected || coreOpenRules[day].default}
                              />
                            </div>
                          </div>
                        </div>
                      }
                      {coreCloseRules &&
                        <div className="pl-3 mt-2 core-schedule-rule">
                          <div className="core-schedule-rule-no-wrap-section">
                            <div className="core-schedule-rule-icon-close">{/* close icon */}
                              <div
                                style={{width: dayNotExpected ? "2rem" : "1.7rem", height: dayNotExpected ? "2rem" : "1.7rem", paddingLeft: dayNotExpected ? "2px" : "0px"  }}
                              >
                                <SVGIcon type={dayNotExpected ? "dash" : "lock"} color={dayNotExpected ? "#2B3E4E" : "#28498A"} />
                              </div>
                            </div>
                            <div className="pl-3 core-schedule-rule-fields">
                              <div className="p-0 core-schedule-early-field-close">{/* early offset */}
                                <Tooltip 
                                  placement="top"
                                  description={`Early offset time: ${coreCloseRules[day].earlyOffsetTime}`}
                                >
                                  <Form.Control
                                    type="number"
                                    name={`close_early_offset_${day}`}
                                    value={dayNotExpected ? 0 : coreCloseRules[day].earlyOffsetMinutes}
                                    onChange={event => changeRuleValue("close", day, "earlyOffsetMinutes", event.target.value)}
                                    readOnly={!editing || dayNotExpected}
                                    style={ coreCloseRules[day].default ? {} : { border: "solid 1px #000" } }
                                  />
                                </Tooltip>
                              </div>
                              <div className="px-2 py-0 core-schedule-ideal-field-close ">{/* ideal event hour */}
                                <Form.Control
                                  type="time"
                                  name={`close_ideal_event_hour_${day}`}
                                  value={dayNotExpected ? "00:00" : coreCloseRules[day].idealEventHour}
                                  onChange={event => changeRuleValue("close", day, "idealEventHour", event.target.value)}
                                  readOnly={!editing || dayNotExpected}
                                  style={ coreCloseRules[day].default ? {} : { border: "solid 1px #000" } }
                                />
                                <div className="core-schedule-next-day-message">{coreCloseRules[day].showNextDayMessage ? "Next day" : ""}</div>
                              </div>
                              <div className="p-0 core-schedule-late-field-close">{/* late offset */}
                                <Tooltip 
                                  placement="top"
                                  description={`Late offset time: ${coreCloseRules[day].lateOffsetTime}`}
                                >
                                  <Form.Control
                                    type="number"
                                    name={`close_late_offset_${day}`}
                                    value={dayNotExpected ? 0 : coreCloseRules[day].lateOffsetMinutes}
                                    onChange={event => changeRuleValue("close", day, "lateOffsetMinutes", event.target.value)}
                                    readOnly={!editing || dayNotExpected}
                                    style={ coreCloseRules[day].default ? {} : { border: "solid 1px #000" } }
                                  />
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div className="my-2 core-schedule-rule-fields-close">
                            <div className="pl-3 py-0 core-schedule-reminder-field">{/* reminder */}
                              <Form.Control
                                name={`close_reminder_${day}`}
                                style={{ width: "1rem", height: "1rem", backgroundColor: "#fff" }}
                                type="checkbox"
                                checked={dayNotExpected ? false : coreCloseRules[day].reminder}
                                onChange={event => changeRuleValue("close", day, "reminder", event.target.checked)}
                                disabled={!editing || dayNotExpected}
                              />
                            </div>
                            <div className="pl-3 py-0 core-schedule-delete-field">{/* delete rule */}
                              <Form.Control
                                name={`close_delete_${day}`}
                                style={{ width: "1rem", height: "1rem", backgroundColor: "#fff" }}
                                type="checkbox"
                                checked={coreCloseRules && coreCloseRules[day].delete}
                                onChange={event => changeRuleValue("close", day, "delete", event.target.checked)}
                                disabled={!editing || dayNotExpected || coreCloseRules[day].default}
                              />
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </Card>
              })
            }
          </div>
        </div>
      </Form>
    </>
}